import { ReactNode, useEffect, useRef, useState } from 'react'

import {
  BillingPrice,
  BillingPriceInterval,
  BillingProduct,
} from '@tribeplatform/gql-client/types'
import { useI18n } from '@tribeplatform/react-components/i18n'
import { Link } from '@tribeplatform/react-components/Link'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'

import { useUpgradePlanMessage } from '../../../../hooks/useUpgradePlanMessage.js'
import { useCurrentProductInformation } from '../hooks/useCurrentProductInformation.js'

import {
  PlanCardButtonLabel,
  type PlanCardButtonLabelVariant,
} from './PlanCardButtonLabel.js'
import { TrialConversionConfirmModal } from './TrialConversionConfirmModal.js'
import { usePlanCardButtonActions } from './usePlanCardButtonActions.js'

interface Props {
  newProduct: BillingProduct
  price: BillingPrice
  className?: string
  label?: ReactNode
}

/**
 * Whether this plan card is the one a trial converts into.
 *
 * With native Stripe trials the network already sits on the paid plan it is evaluating,
 * so the match is on the product itself. Legacy trials sit on a shadow product that
 * points at the paid plan via `upgradedPlanId`.
 *
 * Callers must check this before the "current plan" branch — otherwise a network on a
 * native trial sees its own plan rendered as current and has no way to subscribe.
 */
export const isTrialConversionTarget = ({
  isTrial,
  isManual = false,
  currentProduct,
  newProduct,
}: {
  isTrial: boolean
  /** Account-managed (manual) subscriptions are contact-sales, never self-serve. */
  isManual?: boolean
  currentProduct:
    | Pick<BillingProduct, 'id' | 'upgradedPlanId'>
    | undefined
    | null
  newProduct: Pick<BillingProduct, 'id' | 'isEnterprise'>
}): boolean =>
  Boolean(
    isTrial &&
      // A manual (account-managed) trial is handled by its AE: no self-serve CTA, and no
      // self-serve trial alert either — `useTrialStatus` already suppresses manual trials.
      !isManual &&
      // An enterprise plan is contact-sales, never self-serve. The API reports `isTrial` for
      // any `trialing` subscription — including a hand-configured enterprise trial parked by
      // an AE. The card itself already renders "Contact us" for enterprise plans; this guard
      // keeps the shared predicate honest for everything else that reads it (the trial
      // status alert, the CTA choice), so an AE-parked trial is never described as
      // self-serve convertible. The API fails closed on that path too.
      !newProduct.isEnterprise &&
      (currentProduct?.id === newProduct.id ||
        // LEGACY_TRIAL
        currentProduct?.upgradedPlanId === newProduct.id),
  )

const isNativeTrialConversionTarget = ({
  isTrial,
  isManual,
  currentProduct,
  newProduct,
}: Parameters<typeof isTrialConversionTarget>[0]): boolean =>
  isTrialConversionTarget({ isTrial, isManual, currentProduct, newProduct }) &&
  currentProduct?.id === newProduct.id

/**
 * Shape of a promotion code we will send to the API. Stripe allows 1–500 characters of
 * `A-Z a-z 0-9 -` (case-insensitive); the API accepts 3–50 of the same set, and no code we issue
 * is shorter than 6. Kept in sync with `SubscriptionCheckoutSessionInputDto.promotionCode`.
 */
const PROMOTION_CODE_PATTERN = /^[A-Za-z0-9-]{3,50}$/

export const PlanCardButton = ({
  newProduct,
  price,
  className,
  label,
}: Props) => {
  const { $t } = useI18n()
  const [promotionCode, setPromotionCode] = useState('')
  const [promoError, setPromoError] = useState<string | undefined>(undefined)
  // Which price the card shows *now*; a rejection for a request submitted under the previous
  // price (interval toggled mid-flight) must not land under this one.
  const latestPriceId = useRef(price.id)
  // A rejection belongs to the code it was typed against; a new price is a new attempt. Track
  // the current price and clear a stale rejection in an effect rather than during render.
  useEffect(() => {
    latestPriceId.current = price.id
    setPromoError(undefined)
  }, [price.id])
  const { sendUpgradePlanMessage } = useUpgradePlanMessage()
  const {
    currentProduct,
    currentInterval,
    currentProductOrder,
    isManual,
    isTrial,
  } = useCurrentProductInformation()
  const { autoUpdatePlan, isLoading, openCheckout, switchBillingInterval } =
    usePlanCardButtonActions({
      currentProductOrder,
      newProduct,
      price,
    })

  const newProductOrder = newProduct.order
  // Subscribing cancels the trial before Checkout (Stripe won't sell to a customer with an
  // active subscription), and an abandoned Checkout does not restore it. Confirm first so the
  // cancellation is never a surprise; only the dialog's Continue actually starts it.
  const [confirmOpen, setConfirmOpen] = useState(false)

  const isTrialConversion = isTrialConversionTarget({
    isTrial,
    isManual,
    currentProduct,
    newProduct,
  })
  const isNativeTrialConversion = isNativeTrialConversionTarget({
    isTrial,
    isManual,
    currentProduct,
    newProduct,
  })

  if (isTrialConversion) {
    const handleUpgrade = async () => {
      // Native setup-mode conversion cancels nothing, so we can validate a typed promotion code
      // before redirect. Legacy trials still use subscription-mode Checkout after pre-cancel;
      // leave promotion entry to Stripe's own page for that path.
      setPromoError(undefined)
      const submittedFor = price.id
      const typedCode = promotionCode.trim()
      // Reject a malformed code here, before any request: the API validates the same shape, but
      // its class-validator message is not phrased as a promotion-code rejection and would fall
      // through to the generic toast instead of landing under the field.
      if (
        isNativeTrialConversion &&
        typedCode &&
        !PROMOTION_CODE_PATTERN.test(typedCode)
      ) {
        setPromoError(
          $t({
            defaultMessage:
              'Promotion codes are 3 to 50 letters, numbers, or dashes.',
            id: 'Admin.Billing.Convert.PromoFormat',
          }),
        )
        return
      }
      const { promoError: error } = await openCheckout(
        isNativeTrialConversion ? typedCode || undefined : undefined,
        // Native conversion redirects the current tab (setup-mode returns via success_url);
        // legacy opens a new tab.
        { sameTab: isNativeTrialConversion },
      )
      if (error && latestPriceId.current === submittedFor) {
        setPromoError(error)
      }
    }

    return (
      <div className="flex flex-col gap-2">
        {isNativeTrialConversion && (
          <FormControl.Input
            // Per-price name keeps the input id (its label's `for`) unique across cards.
            name={`promotionCode-${price.id}`}
            // Visually hidden, still a real <label for>: the field keeps its accessible name (and
            // `getByLabelText` in tests) while the card shows only the placeholder.
            label={
              <span className="sr-only">
                {$t({
                  defaultMessage: 'Promotion code',
                  id: 'Admin.Billing.Convert.PromoLabel',
                })}
              </span>
            }
            invalid={!!promoError}
            error={promoError}
            value={promotionCode}
            onChange={e => {
              setPromotionCode(e.currentTarget.value)
              if (promoError) {
                setPromoError(undefined)
              }
            }}
            onKeyDown={e => {
              // Not while an IME is composing, not on key repeat, not while a request is in flight.
              if (
                e.key === 'Enter' &&
                !e.repeat &&
                !e.nativeEvent.isComposing &&
                !isLoading
              ) {
                e.preventDefault()
                handleUpgrade()
              }
            }}
            // Read-only rather than disabled while the request runs: disabling blurs the field,
            // so a rejection would come back with focus on the document body.
            readOnly={isLoading}
            // Mirrors the API's validation (3–50 chars of A–Z, 0–9, dash; Stripe itself allows
            // 1–500 of the same set — the narrower band is ours, no issued code is outside it).
            minLength={3}
            maxLength={50}
            pattern="[A-Za-z0-9\-]{3,50}"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            placeholder={$t({
              defaultMessage: 'Promotion code',
              id: 'Admin.Billing.Convert.PromoPlaceholder',
            })}
          />
        )}
        <Button
          fullWidth
          variant="primary"
          // Native conversion is setup-mode and cancels nothing, so upgrade directly (with any
          // promotion code). A legacy conversion pre-cancels the trial, so confirm first via the
          // dialog; only its Continue calls through to checkout.
          onClick={
            isNativeTrialConversion ? handleUpgrade : () => setConfirmOpen(true)
          }
          loading={isLoading}
          // The ui-kit Button loading state is visual only; without disabled a double click opens
          // two Checkout sessions (native) or the dialog twice (legacy).
          disabled={isLoading}
          className={className}
        >
          {label ?? <PlanCardButtonLabel variant="subscribe" />}
        </Button>
        {!isNativeTrialConversion && (
          <TrialConversionConfirmModal
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            onConfirm={() => {
              setConfirmOpen(false)
              handleUpgrade()
            }}
            isLoading={isLoading}
            newProduct={newProduct}
            price={price}
          />
        )}
      </div>
    )
  }

  if (
    currentProduct?.id === newProduct?.id &&
    currentInterval === price?.interval
  ) {
    return (
      <Button fullWidth variant="secondary" disabled className={className}>
        {label ?? <PlanCardButtonLabel variant="current" />}
      </Button>
    )
  }

  if (currentProduct?.isEnterprise || newProduct.isEnterprise || isManual) {
    return (
      <Button
        fullWidth
        as={Link}
        href="https://bettermode.com/contact-sales"
        variant="primary"
        className={className}
      >
        {label ?? <PlanCardButtonLabel variant="contact" />}
      </Button>
    )
  }

  if (
    currentProduct &&
    currentInterval &&
    price?.interval !== currentInterval
  ) {
    if (newProduct?.id === currentProduct?.id) {
      const labelVariant: PlanCardButtonLabelVariant =
        price?.interval === BillingPriceInterval.Month
          ? 'switch-monthly'
          : 'switch-yearly'

      return (
        <Button
          fullWidth
          variant="primary"
          onClick={() => switchBillingInterval()}
          loading={isLoading}
          className={className}
        >
          {label ?? <PlanCardButtonLabel variant={labelVariant} />}
        </Button>
      )
    }
    if (newProductOrder < currentProductOrder) {
      if (!newProduct.downgradable) {
        return (
          <Button
            fullWidth
            variant="primary"
            onClick={() => sendUpgradePlanMessage(newProduct?.name)}
            loading={isLoading}
            className={className}
          >
            {label ?? <PlanCardButtonLabel variant="contact" />}
          </Button>
        )
      }

      const labelVariant: PlanCardButtonLabelVariant =
        price?.interval === BillingPriceInterval.Month
          ? 'downgrade-switch-monthly'
          : 'downgrade-switch-yearly'

      return (
        <Button
          fullWidth
          variant="primary"
          onClick={async () => {
            await switchBillingInterval({ sendToast: false })
            await autoUpdatePlan()
          }}
          loading={isLoading}
          className={className}
        >
          {label ?? <PlanCardButtonLabel variant={labelVariant} />}
        </Button>
      )
    }

    const labelVariant: PlanCardButtonLabelVariant =
      price?.interval === BillingPriceInterval.Month
        ? 'upgrade-switch-monthly'
        : 'upgrade-switch-yearly'

    return (
      <Button
        fullWidth
        variant="primary"
        onClick={async () => {
          await switchBillingInterval({ sendToast: false })
          await autoUpdatePlan()
        }}
        loading={isLoading}
        className={className}
      >
        {label ?? <PlanCardButtonLabel variant={labelVariant} />}
      </Button>
    )
  }

  if (newProductOrder < currentProductOrder) {
    return (
      <Button
        fullWidth
        variant="primary"
        onClick={() => {
          if (newProduct.downgradable) {
            autoUpdatePlan()
          } else {
            sendUpgradePlanMessage(newProduct?.name)
          }
        }}
        loading={isLoading}
        className={className}
      >
        {label ?? (
          <PlanCardButtonLabel
            variant={newProduct.downgradable ? 'downgrade' : 'contact'}
          />
        )}
      </Button>
    )
  }

  return (
    <>
      <Button
        fullWidth
        variant="primary"
        onClick={() => {
          if (currentProduct) {
            autoUpdatePlan()
          } else if (isTrial) {
            // A trialing network can reach this branch when its current product does not resolve,
            // and `openCheckout` cancels the trial subscription server-side before redirecting.
            // Confirm first so the cancellation is never a surprise — the same guard as the
            // "Subscribe now" path above.
            setConfirmOpen(true)
          } else {
            openCheckout()
          }
        }}
        loading={isLoading}
        className={className}
      >
        {label ?? <PlanCardButtonLabel variant="upgrade" />}
      </Button>
      {isTrial && (
        <TrialConversionConfirmModal
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={() => {
            setConfirmOpen(false)
            openCheckout()
          }}
          isLoading={isLoading}
          newProduct={newProduct}
          price={price}
        />
      )}
    </>
  )
}
